// Import from NPM
// -------------------------------------
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Image, Loader , Button} from "semantic-ui-react";
import _ from "lodash";

// Import from Config
// -------------------------------------
import { useGetCourseQuery, useGetSimDecksQuery } from "@api/apiV6";
import { snaked } from "@utilities/helpers";

// Import Components
// -------------------------------------
import { Div } from "@components/Generics.react";
import MasterLayout from "@layouts/MasterLayout.react";
import ErrorPage from "@components/ErrorPage.react";
import LiquidProgressChart from "@components/charts/LiquidProgressChart.react";
import FavoriteArticles from "@components/FavoriteArticles.react";
import SimIndividualReport from "../reports/panels/SimIndividualReport.react";

export default function SimReports(props) {
    const { inCourse = false } = props;
    const { courseId, courseIdentifier } = useParams();
    const user = useSelector((state) => state.auth.user);
    const { t } = useTranslation("common");

    const {
        data: course,
        isLoading,
        isSuccess,
        isError,
        error,
    } = useGetCourseQuery({
        id: courseId || courseIdentifier,
        user_id: user._id,
    });
    const { data: reports, isSuccess: reportsSuccess } = useGetSimDecksQuery({
        course_id: courseId || courseIdentifier,
        user_id: user._id,
    });
    const [reportView, setReportView] = useState(false);
    const [calcBackbone, setCalcBackbone] = useState({});

    const meta = course?.meta.reportsModel || [];

    function showBlock(thisSection) {
        if (thisSection.blocks != null) {
            if (!inCourse || thisSection.inCourse)
                return (
                    <Div
                        flex
                        ash={thisSection.header !== ""}
                        basepad
                        spaceBetween
                        wd={inCourse ? "100%" : thisSection.width}
                        key={`section-block-${snaked(thisSection.header)}`}
                    >
                        {thisSection.header !== "" && (
                            <Div fluid bold uppercase charcoal padded snug>
                                {thisSection.header}
                            </Div>
                        )}
                        {_.map(
                            _.filter(
                                thisSection.blocks,
                                (b) => !inCourse || b.inCourse
                            ),
                            (section) => showBlock(section)
                        )}
                    </Div>
                );
        } else {
            let showSection = <Div />;
            switch (thisSection.format) {
                case "image":
                    return (
                        <Div fluid rounded noOverflow gutter>
                            {thisSection.header && (
                                <Div fluid white huge basepad center-align>
                                    {thisSection.header}
                                </Div>
                            )}
                            <Image fluid src={thisSection.image} />
                        </Div>
                    );
                case "competencyReport":
                    return (
                        <Div fluid huge padTop>
                            {course.currentState.completion && (
                                <Button
                                    fluid
                                    primary
                                    content="View Summary"
                                    onClick={() => setReportView(true)}
                                />
                            )}
                            <SimIndividualReport
                                user={user}
                                course={course}
                                reports={reports}
                                setOpen={setReportView}
                                open={reportView}
                            />
                        </Div>
                    );
                    return <Div />;
                case "liquidGauge":
                    const rawValue = _.get(reports, thisSection.value);
                    const showValue = _.isArray(rawValue)
                        ? parseInt(_.mean(_.map(rawValue, parseFloat)) * 100, 0)
                        : parseInt(rawValue, 0);
                    return (
                        <Div fluid large center-aligned>
                            <LiquidProgressChart
                                id={`prmLpc${thisSection.header}`}
                                value={showValue}
                                width={160}
                                height={160}
                                subText={thisSection.header}
                            />
                        </Div>
                    );
                case "profit":
                    const profit = parseFloat(
                        (calcBackbone["totRev"] || 0) -
                            (calcBackbone["totExp"] || 0)
                    );
                    let cloneBackbone = _.cloneDeep(calcBackbone);
                    if (cloneBackbone.profit !== profit) {
                        cloneBackbone.profit = profit;
                        setCalcBackbone(cloneBackbone);
                    }
                    return (
                        <Div wd={inCourse ? "90%" : "100%"} centered>
                            <Div
                                fluid
                                danger={profit <= 0}
                                success={profit > 0}
                                big
                                center-align
                                key="profit-block"
                                pureCircle
                                relative
                                dropShadow
                                style={{ height: 0, paddingBottom: "100%" }}
                            >
                                <Div
                                    absolute
                                    style={{
                                        top: "45%",
                                        left: "50%",
                                        transform: "translate(-50%,-50%)",
                                    }}
                                >
                                    <Div fluid center-align>
                                        Profit
                                    </Div>
                                    <Div altText large>
                                        INR {profit.toLocaleString("en-IN")}
                                    </Div>
                                </Div>
                            </Div>
                        </Div>
                    );
                case "table":
                    let total = 0;
                    showSection = _.map(thisSection.data, (row, idx) => {
                        const isTotalRow = row.key === "total";
                        const rowValue = _.get(
                            reports,
                            `${row.deck}.currentState.meta.${row.key}`
                        );
                        if (!isTotalRow) {
                            if (!isNaN(rowValue)) {
                                total += parseInt(rowValue, 0) || 0;
                            }
                        } else {
                            let cloneBackbone = _.cloneDeep(calcBackbone);
                            if (
                                cloneBackbone[row.variable] !== total &&
                                !isNaN(total)
                            ) {
                                cloneBackbone[row.variable] = total;
                                setCalcBackbone(cloneBackbone);
                            }
                        }

                        if ((rowValue && !inCourse) || isTotalRow) {
                            return (
                                <Div
                                    fluid
                                    flex
                                    spaceBetween
                                    snug
                                    small
                                    key={`table-row-${snaked(
                                        thisSection.header
                                    )}-${idx}`}
                                >
                                    <Div
                                        wd="calc(50% - 2.5px)"
                                        white={!isTotalRow}
                                        half={isTotalRow}
                                        compact
                                        bold
                                    >
                                        {row.header}
                                    </Div>
                                    <Div
                                        wd="calc(50% - 2.5px)"
                                        white={!isTotalRow}
                                        half={isTotalRow}
                                        compact
                                        right-aligned
                                    >
                                        {!isTotalRow
                                            ? row.format.replace(
                                                  "$v$",
                                                  isNaN(rowValue)
                                                      ? rowValue
                                                      : parseInt(
                                                            rowValue,
                                                            0
                                                        ).toLocaleString(
                                                            "en-US"
                                                        )
                                              )
                                            : total
                                            ? row.format.replace(
                                                  "$v$",
                                                  parseInt(
                                                      total,
                                                      0
                                                  ).toLocaleString("en-IN")
                                              )
                                            : ""}
                                    </Div>
                                </Div>
                            );
                        } else return <Div key={`table-row-dummy-${idx}`} />;
                    });
                    break;
                default:
            }
            if (!inCourse || thisSection.inCourse)
                return (
                    <Div
                        fluid
                        flex
                        spaceBetween
                        top-aligned
                        wd={inCourse ? "100%" : thisSection.width}
                        key={`section-block-${snaked(thisSection.header)}`}
                    >
                        <Div fluid bold uppercase peat padded snug>
                            {thisSection.header}
                        </Div>
                        {showSection}
                    </Div>
                );
        }
    }

    const reportsPanel = reportsSuccess && (
        <Div
            ht={`calc(100vh - ${inCourse ? 230 : 120}px)`}
            autoOverflowY
            charcoal={!inCourse}
            basepad={!inCourse}
            rounded
        >
            <Div
                fluid={inCourse}
                flex={!inCourse}
                spaceBetween
                small={!inCourse}
                top-aligned
            >
                {_.map(meta, (section) => showBlock(section))}
            </Div>
        </Div>
    );

    // ========================= Render Function =================================
    if (isLoading) return <Loader active />;
    if (isError) return <ErrorPage errorMsg={error.data.response} />;
    if (isSuccess)
        if (inCourse) return reportsPanel;
        else
            return (
                <MasterLayout
                    title={course.name}
                    description={course.description}
                    image={course.image}
                    mainPanel={reportsPanel}
                    rightPanelName={t("course.scoreboard.triggerText")}
                    rightPanel={<FavoriteArticles />}
                    rootPage={false}
                />
            );
    return <div />;
}
